<template>
  <div>
    <b-modal
      id="modal-patient-card-edit"
      :no-close-on-backdrop="true"
      ok-only
      centered
      size="lg"
      :title="
        createMode
          ? $t('reception.add-patient-card')
          : $t('reception.edit-patient-card')
      "
      :ok-title="$t('common.ok')"
      @ok="submitItem()"
    >
      <b-card-text>
        <b-row>
          <b-col md="12">
            <b-form-group
              :label="$t('reception.companion-name')"
              label-for="Name"
            >
              <b-form-input
                id="Name"
                v-model="EditForm.full_companion_name"
                :placeholder="$t('reception.companion-name')"
              />
            </b-form-group>
          </b-col>

          <b-col md="12">
            <b-form-group
              :label="$t('reception.companion-phone')"
              label-for="phoneNumber"
            >
              <VuePhoneNumberInput
                v-model="EditForm.companion_phone"
                default-country-code="IQ"
                :translations="{
                  countrySelectorLabel: $t('common.country-code'),
                  countrySelectorError: $t('common.select-country'),
                  phoneNumberLabel: $t('common.phone'),
                  example: $t('common.example'),
                }"
                :error="
                  EditForm.companion_phone
                    ? isNotValid || EditForm.companion_phone[0] != 0
                    : false
                "
                @update="
                  (obj) => {
                    showPhoneObj(obj, 'countryCallingCode');
                  }
                "
              />
              <p
                v-if="errorForm.companion_phone"
                class="ml-25 error"
              >
                {{ $t("common.field-required") }}
              </p>
            </b-form-group>
          </b-col>
          <b-col cols="12">
            <b-form-group
              :label="$t('reception.card-type')"
              label-for="Type"
            >
              <v-select
                v-model="EditForm.type"
                :options="['IN', 'NICU', 'EMERGENCY', 'CLINIC', 'LAB']"
                :placeholder="$t('reception.card-type')"
              />
            </b-form-group>
          </b-col>

          <!-- Treatment Type for IN patients -->
          <b-col
            v-if="EditForm.type === 'IN'"
            cols="12"
          >
            <b-form-group
              :label="$t('reception.treatment-type')"
              label-for="treatmentType"
            >
              <b-row class="treatment-type-cards">
                <!-- None Option -->
                <b-col
                  md="4"
                  cols="12"
                  class="mb-2"
                >
                  <div
                    class="treatment-card"
                    :class="{ active: treatment_type === 'None' }"
                    @click="treatment_type = 'None'"
                  >
                    <feather-icon
                      icon="XCircleIcon"
                      size="24"
                      class="mb-2"
                    />
                    <div class="treatment-card-title">
                      {{ $t("common.none") }}
                    </div>
                  </div>
                </b-col>

                <!-- Bed Rest Option -->
                <b-col
                  md="4"
                  cols="12"
                  class="mb-2"
                >
                  <div
                    class="treatment-card"
                    :class="{ active: treatment_type === 'Bed Rest' }"
                    @click="treatment_type = 'Bed Rest'"
                  >
                    <feather-icon
                      icon="HomeIcon"
                      size="24"
                      class="mb-2"
                    />
                    <div class="treatment-card-title">
                      {{ $t("reception.bed-rest") }}
                    </div>
                  </div>
                </b-col>

                <!-- Surgery Option -->
                <b-col
                  md="4"
                  cols="12"
                  class="mb-2"
                >
                  <div
                    class="treatment-card"
                    :class="{ active: treatment_type === 'Surgery' }"
                    @click="treatment_type = 'Surgery'"
                  >
                    <feather-icon
                      icon="ActivityIcon"
                      size="24"
                      class="mb-2"
                    />
                    <div class="treatment-card-title">
                      {{ $t("reception.surgery") }}
                    </div>
                  </div>
                </b-col>
              </b-row>
            </b-form-group>
          </b-col>

          <!-- Doctors selection for IN patients -->
          <b-col
            v-if="EditForm.type === 'IN' && treatment_type"
            md="12"
            cols="12"
          >
            <b-form-group :label="$t('common.doctors')">
              <v-select
                v-model="selectedDoctors"
                :options="doctors"
                multiple
                label="name"
                :reduce="(doctor) => doctor"
                :close-on-select="false"
              >
                <template
                  v-if="treatment_type === 'Surgery'"
                  #option="{ name, type }"
                >
                  <b style="margin: 0">
                    {{ name }}
                    <span
                      v-if="type == 'surgeon'"
                      class="text-primary"
                    >
                      ({{ $t("hospital.surgeon") }})
                    </span>
                    <span
                      v-if="type == 'anesthetist'"
                      class="text-primary"
                    >
                      ({{ $t("hospital.anesthetist") }})
                    </span>
                  </b>
                </template>
                <template
                  v-if="treatment_type === 'Surgery'"
                  #selected-option="{ name, type }"
                >
                  <b style="margin: 0">
                    {{ name }}
                    <span
                      v-if="type == 'surgeon'"
                      class="text-success"
                    >
                      ({{ $t("hospital.surgeon") }})
                    </span>
                    <span
                      v-if="type == 'anesthetist'"
                      class="text-success"
                    >
                      ({{ $t("hospital.anesthetist") }})
                    </span>
                  </b>
                </template>
              </v-select>
            </b-form-group>
          </b-col>

          <!-- Surgery selection for IN patients with Surgery treatment -->
          <div
            v-if="EditForm.type === 'IN' && treatment_type === 'Surgery'"
            class="mt-2 mt-md-0 w-100 mx-1"
          >
            <b-row>
              <b-col
                md="12"
                cols="12"
                class="mb-1"
              >
                <div class="my-1">
                  <b-form-group :label="$t('reception.available-surgeries')">
                    <v-select
                      v-model="selectedAvailableSurgeries"
                      :options="availableSurgeriesOptions"
                      :placeholder="$t('reception.available-surgeries')"
                      label="title"
                    >
                      <template #list-footer>
                        <b-button
                          variant="success"
                          size="sm"
                          block
                          type="button"
                          @click.stop.prevent="showCreateSurgeryModal"
                        >
                          {{ $t("reception.create-surgery") }}
                        </b-button>
                      </template>
                    </v-select>
                  </b-form-group>
                </div>
              </b-col>

              <!-- Surgery price input shown when IN patient and Surgery selected -->
              <b-col
                md="12"
                cols="12"
                class="mb-1"
              >
                <b-form-group
                  :label="$t('reception.surgery-price') || 'Surgery Price'"
                >
                  <b-form-input
                    v-model.number="patientSurgeryPrice"
                    type="number"
                    min="0"
                    step="0.01"
                    :placeholder="
                      $t('reception.surgery-price') || 'Surgery Price'
                    "
                  />
                </b-form-group>
              </b-col>

              <!-- Surgery date/time input -->
              <b-col
                md="12"
                cols="12"
                class="mb-1"
              >
                <b-form-group
                  :label="
                    $t('surgery.surgery_date_time') || 'Surgery Date & Time'
                  "
                >
                  <b-form-input
                    v-model="patientSurgeryDateTime"
                    type="datetime-local"
                    :placeholder="
                      $t('surgery.surgery_date_time') || 'Surgery Date & Time'
                    "
                  />
                </b-form-group>
              </b-col>

              <b-modal
                id="modal-create-surgery"
                :title="$t('reception.create-surgery')"
                ok-only
                centered
                size="lg"
                :no-close-on-backdrop="true"
              >
                <div>
                  <b-form-group :label="$t('common.title')">
                    <b-form-input v-model="surgeryForm.title" />
                  </b-form-group>
                  <b-form-group :label="$t('common.description')">
                    <b-form-textarea v-model="surgeryForm.description" />
                  </b-form-group>
                  <b-form-group :label="$t('reception.type')">
                    <v-select
                      v-model="surgeryForm.type"
                      :options="surgeryTypesList"
                      label="text"
                      :reduce="(option) => option.value"
                    />
                  </b-form-group>
                </div>
                <template #modal-footer="{}">
                  <b-button
                    ref="button"
                    variant="primary"
                    class="m-0"
                    :disabled="loadingSurgery"
                    block
                    type="button"
                    @click.prevent="createSurgery"
                  >
                    <span v-if="!loadingSurgery">{{ $t("common.add") }}</span>
                    <b-spinner
                      v-else
                      small
                    />
                  </b-button>
                </template>
              </b-modal>
            </b-row>
          </div>

          <!-- Room selection for IN patients -->
          <b-col
            v-if="EditForm.type === 'IN'"
            md="12"
            cols="12"
          >
            <AutoComplete
              v-model="EditForm.room_id"
              :label="$t('reception.room')"
              :placeholder="$t('reception.room')"
              :serializer="(val) => val.id"
              param-query="search"
              endpoint="available-room"
              text-title="name"
            />
          </b-col>

          <!-- Child name for NICU patients -->
          <b-col
            v-if="EditForm.type === 'NICU'"
            cols="12"
          >
            <b-form-group
              :label="$t('reception.child_name')"
              label-for="child_name"
            >
              <b-form-input
                id="child_name"
                v-model="EditForm.child_name"
                :placeholder="$t('reception.child_name')"
              />
              <small
                style="font-size: 13px"
                class="d-block mt-1 font-weight-bold"
              >
                <b-icon-info-circle class="mr-1" />
                {{ $t("reception.child_name_note") }}
              </small>
            </b-form-group>
          </b-col>

          <!-- Doctors selection for NICU patients -->
          <b-col
            v-if="EditForm.type === 'NICU'"
            md="12"
            cols="12"
          >
            <b-form-group :label="$t('common.doctors')">
              <v-select
                v-model="selectedDoctors"
                :options="doctors"
                multiple
                label="name"
                :reduce="(doctor) => doctor"
                :close-on-select="false"
              />
            </b-form-group>
          </b-col>

          <!-- Incubator selection for NICU patients -->
          <b-col
            v-if="EditForm.type === 'NICU'"
            md="12"
            cols="12"
          >
            <AutoComplete
              v-model="EditForm.incubator_id"
              :label="$t('incubator.incubator')"
              :placeholder="$t('incubator.incubator')"
              param-query="name"
              :serializer="(val) => val.id"
              endpoint="incubators"
              text-title="name"
              include="incubatorType"
              other-query="filter[is_available]=1"
              keyword="name"
            />
          </b-col>

          <!-- Duration Presets for NICU -->
          <b-col
            v-if="EditForm.type === 'NICU'"
            md="12"
          >
            <b-form-group :label="$t('incubator.duration-preset')">
              <b-form-radio-group
                v-model="nicuPresetDuration"
                :options="nicuDurationOptions"
                buttons
                button-variant="outline-primary"
                size="sm"
              />
            </b-form-group>
          </b-col>

          <!-- Duration Days Input for NICU -->
          <b-col
            v-if="EditForm.type === 'NICU'"
            md="6"
            cols="12"
          >
            <b-form-group
              :label="$t('incubator.duration-days')"
              label-for="duration_days"
            >
              <b-form-input
                v-if="nicuPresetDuration === 'custom'"
                id="duration_days"
                v-model.number="customDays"
                type="number"
                min="1"
                :placeholder="$t('incubator.duration-days')"
              />
              <b-form-input
                v-else
                id="duration_days_1"
                :value="1"
                type="number"
                disabled
              />
              <small class="text-muted">
                {{ $t("incubator.enter-number-of-days") }}
              </small>
            </b-form-group>
          </b-col>

          <b-col
            md="6"
            cols="12"
          >
            <b-form-group
              :label="$t('reception.patient-case')"
              label-for="patient_case"
            >
              <b-form-input
                id="patient_case"
                v-model="EditForm.patient_case"
                :placeholder="$t('reception.patient-case')"
              />
            </b-form-group>
          </b-col>
          <b-col
            md="6"
            cols="12"
          >
            <b-form-group
              :label="$t('reception.diagnosis')"
              label-for="diagnosis"
            >
              <b-form-input
                id="diagnosis"
                v-model="EditForm.diagnosis"
                :placeholder="$t('reception.diagnosis')"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </b-card-text>
    </b-modal>
    <b-modal
      id="modal-patient-visit-hospital"
      :no-close-on-backdrop="true"
      ok-only
      centered
      size="lg"
      :title="$t('reception.Add_Visit_To_Hospital_Number')"
      @ok="submitVistNumber()"
    >
      <b-card-text>
        <b-row>
          <b-col md="12">
            <b-form-group
              :label="$t('reception.VisitـHospitalـNumber')"
              label-for="number_of_stays_before_activating_the_system"
            >
              <b-form-input
                id="number_of_stays_before_activating_the_system"
                v-model="number_of_stays_before_activating_the_system"
                placeholder="Enter Visit Hospital Number"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </b-card-text>
    </b-modal>
    <!-- Pending Invoices Warning Modal -->
    <b-modal
      id="modal-pending-invoices-warning"
      :no-close-on-backdrop="true"
      centered
      size="lg"
      :title="$t('reception.pending-invoices-warning') || 'تحذير: فواتير معلقة'"
      @hidden="resetPendingInvoicesState"
    >
      <b-card-text>
        <div
          v-if="loadingPendingInvoices"
          class="text-center py-4"
        >
          <b-spinner variant="primary" />
          <p class="mt-2">
            {{ $t("common.loading") || "جاري التحميل..." }}
          </p>
        </div>

        <div v-else>
          <!-- Patient Info -->
          <div
            v-if="pendingPatientInfo"
            class="mb-3 p-3"
            style="background-color: #f8f9fa; border-radius: 8px;"
          >
            <h5 class="mb-2">
              {{ $t("common.patient-info") || "معلومات المريض" }}
            </h5>
            <p class="mb-1">
              <strong>{{ $t("common.patient-name") || "اسم المريض" }}:</strong> {{ pendingPatientInfo.name }}
            </p>
            <p
              v-if="pendingPatientInfo.patient_no"
              class="mb-1"
            >
              <strong>{{ $t("reception.patient-no") || "رقم المريض" }}:</strong> {{ pendingPatientInfo.patient_no }}
            </p>
            <p
              v-if="pendingPatientInfo.phone"
              class="mb-0"
            >
              <strong>{{ $t("common.phone") || "الهاتف" }}:</strong> {{ pendingPatientInfo.phone }}
            </p>
          </div>

          <!-- Warning Message -->
          <b-alert
            variant="warning"
            show
            class="mb-3"
          >
            <strong>{{ $t("reception.patient-has-pending-invoices") || "هذا المريض لديه فواتير معلقة غير مدفوعة" }}</strong>
          </b-alert>

          <!-- Doctor Selection (only if doctors available) -->
          <div
            v-if="pendingDoctors.length > 0"
            class="mb-3"
          >
            <b-form-group :label="$t('reception.move-invoices-to-doctor') || 'نقل الفواتير المعلقة إلى الطبيب:'">
              <v-select
                v-model="selectedPendingDoctor"
                :options="pendingDoctors"
                label="name"
                :reduce="(doctor) => doctor"
                :placeholder="$t('reception.select-doctor') || 'اختر الطبيب'"
              >
                <template #option="{ name, specialty }">
                  <div>
                    <strong>{{ name }}</strong>
                    <span
                      v-if="specialty"
                      class="text-muted"
                    > - {{ specialty }}</span>
                  </div>
                </template>
              </v-select>
            </b-form-group>
          </div>

          <!-- No Doctors Message -->
          <b-alert
            v-if="pendingInvoices.length > 0 && pendingDoctors.length === 0"
            variant="info"
            show
            class="mb-3"
          >
            {{ $t("reception.no-doctors-available") || "لا يوجد أطباء متاحين لنقل الفواتير إليهم" }}
          </b-alert>

          <!-- Pending Invoices Table -->
          <div v-if="pendingInvoices.length > 0">
            <h6 class="mb-2">
              {{ $t("reception.pending-invoices-list") || "قائمة الفواتير المعلقة:" }}
            </h6>
            <div class="table-responsive">
              <table class="table table-sm table-bordered">
                <thead>
                  <tr>
                    <th>{{ $t("hospital.invoice-no") || "رقم الفاتورة" }}</th>
                    <th>{{ $t("reception.invoice-type") || "نوع الفاتورة" }}</th>
                    <th>{{ $t("employee.amount") || "المبلغ" }}</th>
                    <th>{{ $t("common.status") || "الحالة" }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="invoice in pendingInvoices"
                    :key="invoice.id"
                  >
                    <td>#{{ invoice.invoice_no || invoice.id }}</td>
                    <td>{{ getInvoiceTypeLabel(invoice.type) }}</td>
                    <td>{{ formatPrice(invoice.amount) }} IQD</td>
                    <td>
                      <b-badge variant="warning">
                        {{ $t("common.pending") || "معلق" }}
                      </b-badge>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </b-card-text>

      <template #modal-footer>
        <div class="w-100 d-flex gap-2">
          <b-button
            v-if="pendingDoctors.length > 0 && selectedPendingDoctor"
            variant="primary"
            :disabled="loadingPendingInvoices || !selectedPendingDoctor"
            @click="showMoveInvoicesConfirmation"
          >
            {{ $t("reception.move-invoices-and-stop-card") || "نقل الفواتير وإيقاف البطاقة" }}
          </b-button>
          <b-button
            variant="secondary"
            :disabled="loadingPendingInvoices"
            @click="confirmStopCardWithoutMoving"
          >
            {{ $t("reception.stop-card-without-moving") || "إيقاف البطاقة دون نقل الفواتير" }}
          </b-button>
          <b-button
            v-if="pendingDoctors.length === 0"
            variant="primary"
            :disabled="loadingPendingInvoices"
            @click="confirmStopCardWithoutMoving"
          >
            {{ $t("reception.continue-stop-card") || "متابعة إيقاف البطاقة" }}
          </b-button>
          <b-button
            variant="outline-secondary"
            :disabled="loadingPendingInvoices"
            @click="$bvModal.hide('modal-pending-invoices-warning')"
          >
            {{ $t("common.cancel") || "إلغاء" }}
          </b-button>
        </div>
      </template>
    </b-modal>

    <!-- Confirmation Modal for Moving Invoices -->
    <b-modal
      id="modal-confirm-move-invoices"
      :no-close-on-backdrop="true"
      centered
      size="lg"
      title="تأكيد نقل الفواتير"
    >
      <b-card-text>
        <div class="text-center mb-2">
          <feather-icon
            icon="AlertTriangleIcon"
            size="48"
            class="text-warning"
          />
        </div>
        <h5 class="text-center mb-3 text-danger">
          يستم نقل هذه الفواتير الى الطبيب باعتبارها عمليه شاملة على الطبيب
          <br>
          هل انت متاكد من هذا الاجراء ؟
        </h5>

        <!-- Doctor & Total Info - Compact -->
        <div class="mb-3 p-2" style="background-color: #e3f2fd; border-radius: 8px; border-right: 4px solid #2196F3;">
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <strong>الطبيب:</strong>
              <span class="ml-2">{{ selectedPendingDoctor ? selectedPendingDoctor.name : '-' }}</span>
            </div>
            <div>
              <strong>المجموع:</strong>
              <span class="ml-2 text-primary font-weight-bold" style="font-size: 1.2rem;">{{ formatPrice(pendingInvoicesTotalAmount) }} دينار</span>
            </div>
          </div>
        </div>

        <!-- Invoices Table - Compact -->
        <div class="mb-2">
          <h6 class="mb-2">
            <strong>الفواتير ({{ pendingInvoices ? pendingInvoices.length : 0 }}):</strong>
          </h6>
          <div class="table-responsive">
            <table class="table table-sm table-bordered mb-0">
              <thead class="thead-light">
                <tr>
                  <th style="width: 40px;">#</th>
                  <th>رقم الفاتورة</th>
                  <th>النوع</th>
                  <th class="text-right" style="width: 120px;">المبلغ</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(invoice, index) in pendingInvoices"
                  :key="invoice.id"
                >
                  <td class="text-center">{{ index + 1 }}</td>
                  <td>#{{ invoice.invoice_no || invoice.id }}</td>
                  <td>{{ getInvoiceTypeLabel(invoice.type) }}</td>
                  <td class="text-right font-weight-bold">{{ formatPrice(invoice.amount) }}</td>
                </tr>
              </tbody>
              <tfoot class="bg-light">
                <tr>
                  <td colspan="3" class="text-right">
                    <strong>المجموع الكلي:</strong>
                  </td>
                  <td class="text-right text-primary">
                    <strong>{{ formatPrice(pendingInvoicesTotalAmount) }} دينار</strong>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </b-card-text>

      <template #modal-footer>
        <div class="w-100 d-flex justify-content-between">
          <b-button
            variant="outline-secondary"
            @click="$bvModal.hide('modal-confirm-move-invoices')"
          >
            إلغاء
          </b-button>
          <b-button
            variant="success"
            @click="confirmMoveInvoicesAndStopCard"
          >
            <feather-icon
              icon="CheckIcon"
              class="mr-1"
            />
            تأكيد نقل الفواتير
          </b-button>
        </div>
      </template>
    </b-modal>

    <b-modal
      id="modal-confirmation-stop-card"
      :no-close-on-backdrop="true"
      ok-only
      centered
      size="lg"
      :title="$t('reception.stop-card')"
      @ok="stopCard"
    >
      <b-card-text>
        <b-row>
          <b-col md="12">
            <h3 class="text-center text-danger">
              {{ $t("reception.stop-card-message") }}
            </h3>
          </b-col>
        </b-row>
      </b-card-text>
      <template #modal-footer>
        <b-button
          variant="primary"
          block
          @click="stopCard"
        >{{
          $t("reception.stop-card")
        }}</b-button>
      </template>
    </b-modal>

    <b-card>
      <b-button
        v-if="
          $store.state.app.permissions.includes('store-patientCard') &&
            patientCard &&
            patientCard.type === 'IN'
        "
        variant="warning"
        class="p-1 mb-1"
        block
        @click="changeRoom()"
      >
        {{ $t("reception.change-patient-room") }}
      </b-button>
      <b-button
        v-if="
          $store.state.app.permissions.includes('store-patientCard') &&
            surgeries &&
            surgeries.length > 0
        "
        variant="outline-warning"
        class="p-1 mb-1"
        block
        @click="changeSurgery()"
      >
        {{ $t("common.change-surgery") }}
      </b-button>
      <b-button
        v-if="$store.state.app.permissions.includes('store-patientCard')"
        variant="success"
        class="p-1 mb-1"
        block
        @click="show(EditForm, 'create')"
      >
        {{ $t("reception.add-patient-card") }}
      </b-button>
      <!-- Button: Create card -->

      <b-row v-if="patientCard">
        <b-col
          cols="12"
          md="12"
        >
          <!-- Button: stop card -->
          <b-button
            variant="danger"
            class="p-1"
            block
            @click="showConfirmationModal"
          >
            {{ $t("reception.stop-card") }}
          </b-button>
        </b-col>
        <!-- Button: go to invouce -->
        <!-- <b-col cols="12" class="mt-1">
          <b-button
            v-if="
              $store.state.app.permissions.includes('get-invoice') &&
              showPrivewInvoice
            "
            variant="outline-secondary"
            block
            class="p-1"
            @click="
              $router.push({
                name: 'invoices',
                params: { patientId: patientCard.patient_id },
                query: {
                  patientCardId: patientCard.id,
                },
              })
            "
          >
            {{ $t("hospital.preview-invoices") }}
          </b-button>
        </b-col> -->
      </b-row>
      <b-button
        v-if="patientCardData && !patientCard"
        variant="warning"
        class="p-1 mb-1 mt-1"
        block
        @click="printInpatientForm"
      >
        {{ $t("reception.print-inpatient-form") }}
      </b-button>
      <b-button
        v-if="patientCardData && !patientCard"
        variant="primary"
        class="p-1 mb-1"
        block
        @click="showVistNumberModel()"
      >
        {{ $t("reception.add-patient-visit-number") }}
      </b-button>
    </b-card>
    <ReceptionPatientCardWithQR
      :patient-card="latest_unactive_patient_card"
      :is_unactive_card_qr="true"
    />
    <vue-html2pdf
      v-if="patientCardData && !patientCard"
      ref="currentPatientPdf"
      :show-layout="false"
      :float-layout="true"
      :enable-download="false"
      :preview-modal="true"
      :paginate-elements-by-height="1400"
      filename="patient card"
      :pdf-quality="2"
      :manual-pagination="false"
      pdf-format="a4"
      pdf-orientation="portrait"
      pdf-content-width="800px"
      @hasStartedGeneration="hasStartedGeneration()"
      @hasGenerated="hasGenerated($event)"
    >
      <section
        slot="pdf-content"
        dir="rtl"
        class="pdf-content font-modify my-5 mx-5"
      >
        <b-row class="mx-5 my-3">
          <b-col
            cols="6"
            class="d-flex flex-column align-items-end"
          >
            <span>{{ $brand.nameArFull }}</span>
            <span class="mx-1">دائرة صحة الرصافة</span>
          </b-col>
          <b-col
            cols="6"
            class="d-flex flex-column"
          >
            <span> دائرة الصحة </span>
            <span>دائرة التخطيط واتمتة الموارد</span>
            <span>قسم الاصحاء الصحي والحياتي</span>
            <span>خاص بالحاسبة الالكترونية</span>
          </b-col>
        </b-row>
        <!-- <div class="d-flex flex-column align-items-end mx-5 text-center">s</div> -->
        <table class="border border-2 border-dark">
          <b-row class="mx-0 px-0 d-flex flex-column justify-content-end">
            <b-col cols="12">
              <b-row>
                <b-col
                  class="border border-2 border-dark d-flex justify-content-center align-items-center"
                  cols="1"
                >
                  <span
                    class="rotated-text"
                  >تملئ المعلومات من قبل الممرضة</span>
                </b-col>
                <b-col
                  style="margin: 0; padding: 0"
                  cols="11"
                >
                  <div>
                    <!-- dsdsds -->
                    <div class="start-box">
                      <section>
                        <div class="col-12 px-0 d-flex justify-content-center">
                          <div class="mx-5">
                            استمارة المريض الراقد
                          </div>
                        </div>
                        <div
                          class="col-12 d-flex"
                          style="margin-top: 1px"
                        >
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark"
                          />
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark"
                          />
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark"
                          />
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark"
                          />
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark"
                          />
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark"
                          />
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark"
                          />
                        </div>

                        <div class="col-12 d-flex justify-content-center">
                          1 -
                          <div>رقم التسجيل الطبي للمريض</div>
                        </div>
                        <div
                          class="col-12 d-flex"
                          style="margin-top: 4px"
                        >
                          <div
                            v-for="(digit, index) in paddedPatientNo"
                            :key="index"
                            style="
                              width: 30px !important;
                              height: 20px;
                              font-weight: bold;
                            "
                            class="square-dark border border-2 border-dark text-center font-bold"
                          >
                            {{ digit }}
                          </div>
                        </div>
                        <!-- <div class="col-12 d-flex" style="margin-top: 4px">
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark"
                          >
                            {{ patientCardData.patient.patient_no }}
                          </div>
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark"
                          ></div>
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark"
                          ></div>
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark"
                          ></div>
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark"
                          ></div>
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark"
                          ></div>
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark"
                          ></div>
                        </div> -->
                      </section>
                      <div class="col-8 mb-2">
                        <table style="color: #000">
                          <tr>
                            <td>
                              <span>2 -</span>
                              <span> رقم الوحدة الطبية </span>
                            </td>
                            <td>
                              <span>3 -</span>
                              <span> اختصاص الوحدة الطبية</span>
                            </td>
                          </tr>
                        </table>
                      </div>
                    </div>
                    <!-- dsdsds -->
                  </div>

                  <!-- line one -->

                  <table
                    class="mt-4 font-modify"
                    style="color: #000; width: 100%"
                  >
                    <tr class="pdf-table">
                      <th class="no-border">
                        4-الموقع:{{ patientCardData.patient.district_name }}
                      </th>
                      <th class="no-border">
                        الناحية: {{ patientCardData.patient.town_name }}
                      </th>
                      <th class="no-border">
                        القضاء: {{ patientCardData.patient.judiciary_name }}
                      </th>
                      <th class="no-border font-modify">
                        المحافظة:
                        {{ governorateName }}
                      </th>
                      <td class="square-dark" />
                      <td class="square-dark" />
                      <td class="square-dark" />
                      <td class="square-dark" />
                      <td class="square-dark" />
                      <td class="square-dark" />
                      <td />
                    </tr>
                  </table>
                  <!-- end line one -->
                  <!-- line two -->
                  <table style="width: 100%">
                    <div
                      class="d-flex justify-content-between w-100"
                      style="color: #000"
                    >
                      <div class="no-border w-50 text-left px-1">
                        5-اسم المريض الرباعي: {{ patientCardData.patient.name }}
                      </div>

                      <div
                        class="square-dark border border-2 border-dark w-25"
                        style="height: 20px"
                      />
                    </div>
                  </table>
                  <!-- end line two -->
                  <!-- line three -->

                  <table style="width: 100%">
                    <div
                      class="d-flex justify-content-between w-100"
                      style="color: #000"
                    >
                      <div class="no-border w-50 text-left px-1">
                        6-اسم الام الثلاثي:
                        {{ patientCardData.patient.mother_name }}
                      </div>

                      <div
                        class="square-dark border border-2 border-dark w-25"
                        style="height: 20px"
                      />
                    </div>
                  </table>
                  <table style="width: 100%">
                    <div
                      class="w-100"
                      style="color: #000"
                    >
                      <div class="row no-gutters">
                        <div class="col-8 d-flex justify-content-evenly">
                          <div class="no-border mx-1">
                            7-الجنس:
                            {{
                              patientCardData.patient.gender === "Male"
                                ? "ذكر"
                                : "انثى"
                            }}
                          </div>
                          <div class="no-border mx-2">
                            8-التحصيل الدراسي:
                            {{ patientCardData.patient.education_level }}
                          </div>
                          <div class="no-border mx-2">
                            9-المولد: {{ patientCardData.patient.birth_date }}
                          </div>
                        </div>
                        <div class="col-4 d-flex">
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark"
                          />
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark"
                          />
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark"
                          />
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark"
                          />
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark"
                          />
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark"
                          />
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark"
                          />
                        </div>
                      </div>
                    </div>
                  </table>

                  <!-- end line 3 -->
                  <!-- line 4 -->

                  <!-- end line 4 -->
                  <!-- line 5 -->
                  <table style="width: 100%">
                    <div
                      class="w-100"
                      style="color: #000"
                    >
                      <div class="row no-gutters">
                        <div class="col-8 d-flex justify-content-evenly">
                          <div class="no-border mx-1">
                            10-العمر: {{ patientAge }}
                          </div>
                          <div class="no-border mx-2">
                            11-الحالة الزوجية:
                            {{ patientCardData.patient.marital_status }}
                          </div>
                        </div>
                        <div class="col-4 d-flex">
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark"
                          />
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark"
                          />
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark"
                          />
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark"
                          />
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark"
                          />
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark"
                          />
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark"
                          />
                        </div>
                      </div>
                    </div>
                  </table>

                  <table style="color: #000; width: 100%">
                    <tr class="pdf-table">
                      <th class="no-border">
                        12-محل الاقامة:
                      </th>
                      <th
                        v-if="patientCardData.patient.residence_type === 'city'"
                        class="no-border"
                      >
                        مدن
                      </th>
                      <th
                        v-if="
                          patientCardData.patient.residence_type ===
                            'countryside'
                        "
                        class="no-border"
                      >
                        ريف
                      </th>
                      <th class="no-border">
                        المحافظة :
                        <span>{{ governorateName }}</span>
                      </th>
                      <th class="no-border">
                        القضاء :
                        <span>{{
                          patientCardData.patient.judiciary_name
                        }}</span>
                      </th>
                      <th class="no-border">
                        الناحية
                        <span>{{ patientCardData.patient.town_name }}</span>
                      </th>
                      <td class="square-dark" />
                      <td />
                      <td class="square-dark" />
                      <td class="square-dark" />
                      <td class="square-dark" />
                      <td class="square-dark" />
                      <td class="square-dark" />
                    </tr>
                  </table>
                  <!-- end line 6 -->
                  <!-- line 7-->
                  <table style="width: 100%">
                    <div
                      class="w-100"
                      style="color: #000"
                    >
                      <div class="row no-gutters py-1">
                        <div class="col-8 d-flex justify-content-evenly">
                          <div
                            class="no-border mx-1 d-flex flex-column align-items-start justify-content-between"
                          >
                            13-السجل المدني :

                            <div>
                              14- رقم البطاقة الموحدة:
                              {{ patientCardData.patient.national_id }}
                            </div>
                          </div>
                          <div
                            class="no-border mr-2 d-flex flex-column align-items-start justify-content-between"
                          >
                            <div>
                              رقم الصحيفة:
                              {{ patientCardData.patient.document_page_number }}
                            </div>
                            <div>
                              رقم السجل:
                              {{ patientCardData.patient.document_number }}
                            </div>
                            <div>
                              دائره الاحوال المدنية:
                              {{
                                patientCardData.patient
                                  .department_of_civil_status
                              }}
                            </div>
                          </div>
                          <div class="no-border mx-2">
                            الجنسية: {{ patientCardData.patient.national_id }}
                          </div>
                        </div>
                        <div class="col-4 d-flex align-items-center">
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark"
                          />
                        </div>
                      </div>
                    </div>
                  </table>

                  <!-- end line 7-->
                  <!-- line 8 -->
                  <table style="width: 100%">
                    <div
                      class="w-100"
                      style="color: #000"
                    >
                      <div class="row no-gutters">
                        <div class="col-8 d-flex justify-content-evenly">
                          <div class="no-border mx-1">
                            15-تاريخ الدخول:
                            {{ formatDate(patientCardData.created_at) }}
                          </div>
                        </div>
                        <div class="col-4 d-flex">
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark"
                          />
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark"
                          />
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark"
                          />
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark"
                          />
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark"
                          />
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark"
                          />
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark"
                          />
                        </div>
                      </div>
                    </div>
                  </table>

                  <!-- end line 8 -->
                  <!-- line 9-->
                  <table style="width: 100%">
                    <div
                      class="w-100"
                      style="color: #000"
                    >
                      <div class="row no-gutters py-1">
                        <div class="col-8 d-flex justify-content-evenly">
                          <div class="no-border mx-1">
                            16-هل المريض:
                          </div>
                          <div
                            class="no-border mx-2 d-flex flex-column align-items-start justify-content-between"
                            style="height: 70px"
                          >
                            <div>مدخن</div>
                            <div>مصاب بالسكر</div>
                            <div class="no-border">
                              مصاب بارتفاع ضغط الدم
                            </div>
                            <div class="no-border">
                              لدية امراض القلب
                            </div>
                          </div>
                          <div
                            class="no-border mx-2 d-flex flex-column justify-content-between"
                            style="height: 70px"
                          >
                            <div
                              v-for="i in 4"
                              :key="i"
                            >
                              نعم
                            </div>
                          </div>
                          <div
                            class="no-border mx-2 d-flex flex-column justify-content-between"
                            style="height: 70px"
                          >
                            <div
                              v-for="i in 4"
                              :key="i"
                            >
                              لا
                            </div>
                          </div>
                        </div>
                        <div class="col-4">
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark text-center"
                          >
                            {{
                              latest_unactive_patient_card.is_smoker
                                ? "نعم"
                                : "لا"
                            }}
                          </div>
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark text-center"
                          >
                            {{
                              latest_unactive_patient_card.has_diabetes
                                ? "نعم"
                                : "لا"
                            }}
                          </div>
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark text-center"
                          >
                            {{
                              latest_unactive_patient_card.has_high_blood_pressure
                                ? "نعم"
                                : "لا"
                            }}
                          </div>
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark text-center"
                          >
                            {{
                              latest_unactive_patient_card.has_heart_disease
                                ? "نعم"
                                : "لا"
                            }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </table>

                  <!-- end line 9-->
                  <!-- line 10 -->
                  <table style="width: 100%">
                    <div
                      class="w-100"
                      style="color: #000"
                    >
                      <div class="row no-gutters">
                        <div class="col-8 d-flex justify-content-evenly">
                          <div class="no-border mx-1">
                            17-وزن المريض: {{ patientCardData.patient.weight }}
                          </div>
                        </div>
                        <div class="col-4 d-flex">
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark"
                          />
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark"
                          />
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark"
                          />
                        </div>
                      </div>
                    </div>
                  </table>

                  <!-- end line 10 -->
                </b-col>
              </b-row>
              <b-row>
                <b-col
                  class="border border-2 border-dark d-flex justify-content-center align-items-center"
                  cols="1"
                >
                  <span class="rotated-text">
                    تملئ المعلومات من قبل الطبيب المعالج
                  </span>
                </b-col>
                <b-col
                  style="margin: 0; padding: 0"
                  cols="11"
                >
                  <!-- line 11 -->
                  <table style="width: 100%">
                    <div
                      class="w-100"
                      style="color: #000"
                    >
                      <div class="row no-gutters">
                        <div class="col-8 d-flex justify-content-evenly">
                          <div class="no-border mx-1">
                            18-تاريخ الخروج:
                            {{
                              formatDate(
                                latest_unactive_patient_card.out_date_time
                              )
                            }}
                          </div>
                        </div>
                        <div class="col-4 d-flex">
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark"
                          />
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark"
                          />
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark"
                          />
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark"
                          />
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark"
                          />
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark"
                          />
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark"
                          />
                        </div>
                      </div>
                    </div>
                  </table>

                  <!-- line 12 -->
                  <table style="width: 100%; height: 50px">
                    <div
                      class="w-100"
                      style="color: #000"
                    >
                      <div class="row no-gutters">
                        <div class="col-8 d-flex justify-content-evenly">
                          <div class="no-border mx-1 font-modify">
                            19- التشخيص النهائي للمرض حسب التصنيف الدولي للامراض
                            : ICD-10: {{ patientCardData.patient_case }}
                          </div>
                        </div>
                        <div class="col-4 d-flex">
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark"
                          />
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark"
                          />
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark"
                          />
                        </div>
                      </div>
                    </div>
                  </table>

                  <table style="width: 100%">
                    <div
                      class="w-100"
                      style="color: #000"
                    >
                      <div class="row no-gutters pb-1">
                        <div class="col-8 d-flex justify-content-evenly">
                          <div class="no-border mx-1">
                            20-العمليات الجراحية
                          </div>
                          <div
                            class="no-border mr-2 d-flex flex-column align-items-start justify-content-between"
                          >
                            <div>أ-تاريخ اجرائها</div>
                            <div>ب- نوعها</div>
                            <div>ج-درجتها</div>
                          </div>
                        </div>
                        <div class="col-4 d-flex">
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark"
                          />
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark"
                          />
                          <div
                            style="width: 30px !important; height: 20px"
                            class="border border-2 border-dark"
                          />
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark"
                          />
                        </div>
                      </div>
                    </div>
                  </table>

                  <!-- end line 13-->
                  <!-- line 14 -->
                  <table style="width: 100%">
                    <div
                      class="w-100"
                      style="color: #000"
                    >
                      <div class="row no-gutters">
                        <div class="col-8 d-flex justify-content-evenly">
                          <div class="no-border mx-1">
                            صغرى - 1
                          </div>
                          <div class="no-border mx-1">
                            متوسطة - 2
                          </div>
                          <div class="no-border mx-1">
                            كبرى - 3
                          </div>
                          <div class="no-border mx-1">
                            فوق الكبرى - 4
                          </div>
                          <div class="no-border mx-1">
                            خاصة - 5
                          </div>
                        </div>

                        <div class="col-4 d-flex">
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark"
                          />
                        </div>
                      </div>
                    </div>
                  </table>

                  <!-- end line 14 -->
                  <!-- line 15-->
                  <table style="width: 100%">
                    <div
                      class="w-100"
                      style="color: #000"
                    >
                      <div class="row no-gutters py-1">
                        <div class="col-8 d-flex justify-content-evenly">
                          <div class="no-border mx-1">
                            21- حالة المريض بعد الخروج من المستشفى:
                          </div>
                          <div
                            class="no-border mx-2 d-flex flex-column align-items-start justify-content-between"
                          >
                            <div>1 - شفاء</div>
                            <div>2 - تحسن</div>
                            <div>3 - وفاة</div>
                          </div>
                          <div
                            class="no-border mx-1 d-flex flex-column align-items-start justify-content-between"
                          >
                            <div>4 - خرج على مسؤوليتة</div>
                            <div>5 - نقل الى مسشتفى اخرى</div>
                            <div>6 - حاله اخرى</div>
                          </div>
                        </div>
                        <div class="col-4 d-flex align-items-center">
                          <div
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark"
                          />
                        </div>
                      </div>
                    </div>
                  </table>

                  <!-- end line 15-->
                </b-col>
              </b-row>
              <!-- line 16 -->
              <b-row>
                <b-col
                  class=""
                  cols="1"
                />
                <b-col
                  style="margin: 0; padding: 0"
                  cols="12"
                >
                  <table style="width: 100%">
                    <div
                      class="w-100"
                      style="color: #000"
                    >
                      <div class="row no-gutters">
                        <div class="col-8 d-flex justify-content-evenly">
                          <div class="no-border mx-1">
                            22- مجموع الرقودات السابقة للمريض خلال السنة في هذه
                            المسشتفى :
                          </div>
                        </div>
                        <div class="col-4 d-flex">
                          <div
                            v-for="num in visitNumber"
                            :key="num"
                            style="width: 30px !important; height: 20px"
                            class="square-dark border border-2 border-dark text-center"
                          >
                            {{ num }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </table>

                  <!-- end line 16 -->
                  <!-- line 17 -->
                  <table style="color: #000; width: 100%; height: 50px">
                    <tr>
                      <th
                        colspan="1"
                        class="no-border"
                      >
                        اسم توقيع الممرضة المسؤولة
                      </th>
                      <th
                        colspan="1"
                        class="no-border"
                      >
                        اسم توقيع مسؤول الوحدة الطبية
                      </th>
                    </tr>
                  </table>
                  <!-- end line 17 -->
                </b-col>
              </b-row>
            </b-col>
          </b-row>
        </table>
        <b-row>
          <b-col cols="10">
            <div style="text-align: start">
              <div class="my-3">
                ملاحظة
              </div>
              <div class="my-3">
                ملاحظة
              </div>
            </div>
          </b-col>
          <b-col
            cols="2"
            class="mt-2"
          >
            <div style="">
              <qrcode
                class="qrcodeStyle"
                :size="100"
                :text="`${$brand.publicUrl}/patient-card-by-qr/${latest_unactive_patient_card.id}`"
              />
              <p
                class="text-wrap text-right"
                style="font-size: 10px"
              >
                ملاحظة : هذة QR خاص بأخر بطاقة تم إيقافها للمريض
              </p>
            </div>
          </b-col>
        </b-row>
      </section>
    </vue-html2pdf>

    <b-modal
      id="modal-change-room"
      :no-close-on-backdrop="true"
      ok-only
      centered
      size="lg"
      :title="$t('reception.change-patient-room')"
      @ok="submitChangeRoom()"
    >
      <b-card-text>
        <b-row>
          <b-col lg="6">
            <b-form-group
              :label="$t('reception.room')"
              label-for="room"
            >
              <v-select
                v-model="changeRoomForm.room_id"
                :options="availableRooms"
                label="name"
                :reduce="(room) => room.id"
                :placeholder="$t('reception.select-room')"
              />
            </b-form-group>
          </b-col>
          <b-col lg="6">
            <b-form-group
              :label="$t('reception.days')"
              label-for="days"
            >
              <b-form-input
                id="days"
                v-model="changeRoomForm.days"
                type="number"
                :placeholder="$t('reception.days')"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </b-card-text>
    </b-modal>
    <b-modal
      id="modal-change-surgery"
      :title="$t('common.change-surgery')"
      ok-only
      centered
      size="lg"
      :no-close-on-backdrop="true"
    >
      <b-card-text>
        <b-row>
          <b-col>
            <h3 class="text-center">
              {{ $t("common.patient-surgery") }} :
              <span style="font-size: medium">{{
                surgeries && surgeries.length > 0
                  ? getNestedProperty(
                    surgeries[surgeries.length - 1],
                    "surgery.title"
                  )
                  : $t("common.no-surgery")
              }}</span>
            </h3>
          </b-col>
          <b-col
            md="12"
            cols="12"
            class="mt-3"
          >
            <b-form-group :label="$t('surgery.surgery')">
              <v-select
                v-model="selectedSurgery"
                :options="availableSurgeriesOptions"
                :placeholder="$t('surgery.choose-surgery')"
                label="title"
                :reduce="(option) => option.id"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </b-card-text>
      <template #modal-footer="{}">
        <b-button
          variant="primary"
          class="m-0"
          :disabled="loadingChangeSurgery"
          block
          @click="submitChangeSurgery"
        >
          <span v-if="!loadingChangeSurgery">{{ $t("common.save") }}</span>
          <b-spinner
            v-else
            small
          />
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BModal,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormRadioGroup,
  BSpinner,
  BIconInfoCircle,
  BAlert,
  BBadge,
  BCardText,
} from 'bootstrap-vue'
import VuePhoneNumberInput from 'vue-phone-number-input'
import 'vue-phone-number-input/dist/vue-phone-number-input.css'
import vSelect from 'vue-select'
import VueHtml2pdf from 'vue-html2pdf'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import VueQRCodeComponent from 'vue-qrcode-component'
import ReceptionPatientCardWithQR from '@/components/reception/ReceptionPatientCardWithQR.vue'

const AutoComplete = () => import('@/components/common/AutoComplete.vue')

export default {
  components: {
    ToastificationContent,
    BRow,
    BCol,
    BCard,
    BButton,
    BFormGroup,
    BFormInput,
    BFormRadioGroup,
    BIconInfoCircle,
    VuePhoneNumberInput,
    BFormTextarea,
    vSelect,
    VueHtml2pdf,
    AutoComplete,
    qrcode: VueQRCodeComponent,
    ReceptionPatientCardWithQR,
    BSpinner,
    BAlert,
    BBadge,
    BCardText,
    BModal,
  },
  props: ['patientCard', 'surgeries'],

  data() {
    return {
      EditForm: {
        full_companion_name: '',
        companion_phone: '',
        patient_case_details: '',
        type: '',
        patient_id: '',
        _method: 'PUT',
        doctors: [],
        room_id: '',
        child_name: '',
        incubator_id: '',
        doctor_incubator_id: '',
        start_time: '',
        end_time: '',
      },
      patientCardData: {},
      createMode: false,
      errorForm: {
        full_companion_name: '',
        companion_phone: '',
        patient_case_details: '',
        type: '',
        patient_id: '',
      },
      params: {
        form: {},
        path: '',
      },
      isNotValid: false,
      number_of_total_stays_in_hospital: 0,
      number_of_stays_before_activating_the_system: 0,
      doctors: [],
      selectedDoctors: [],
      treatment_type: '',
      nicuPresetDuration: '1day',
      nicuDurationOptions: [
        { text: `1 ${this.$t('incubator.day')}`, value: '1day' },
        { text: this.$t('incubator.custom'), value: 'custom' },
      ],
      customDays: 1, // Default custom days
      availableSurgeries: [],
      availableSurgeriesOptions: [],
      selectedAvailableSurgeries: '',
      surgeryForm: {
        title: '',
        description: '',
        type: 0,
        price: 0,
      },
      patientSurgeryPrice: 0,
      patientSurgeryDateTime: '',
      loadingSurgery: false,
      paramsForDoctorSurgery: {
        form: {
          doctor_id: '',
          surgery_id: '',
          price: 0,
        },
        path: '/doctorSurgery',
      },
      paramsForSurgery: {
        form: {
          title: '',
          description: '',
          type: 0,
          price: 0,
        },
        path: '/surgery',
      },
      surgeryTypesList: [
        {
          text: 'كبرى ',
          value: '1',
        },
        {
          text: 'صغرى ',
          value: '2',
        },
        {
          text: 'متوسطة',
          value: '3',
        },
        {
          text: 'فوق الكبرى',
          value: '4',
        },
        {
          text: 'خاصة',
          value: '5',
        },
      ],
      currentDoctor: '',
      showPrivewInvoice: false,
      // Pending invoices state
      pendingInvoices: [],
      pendingDoctors: [],
      pendingPatientInfo: null,
      selectedPendingDoctor: null,
      loadingPendingInvoices: false,
      pendingInvoicesError: null,
      latest_unactive_patient_card: '',
      governorateName: '',
      changeRoomForm: {
        room_id: null,
        days: 0,
      },
      availableRooms: [],
      selectedSurgery: null,
      loadingChangeSurgery: false,
    }
  },
  computed: {
    paddedPatientNo() {
      const patientNo = this.patientCardData.patient.patient_no.toString()
      const paddedNo = patientNo.padStart(7, '   ')
      return paddedNo.split('').reverse()
    },
    visitNumber() {
      const patientNo = this.number_of_total_stays_in_hospital.toString()
      const paddedNo = patientNo.padStart(2, '   ')
      return paddedNo.split('').reverse()
    },
    patientAge() {
      if (!this.patientCardData.patient.birth_date) return null
      const birthDate = new Date(this.patientCardData.patient.birth_date)
      const today = new Date()
      let age = today.getFullYear() - birthDate.getFullYear()
      const monthDifference = today.getMonth() - birthDate.getMonth()
      if (
        monthDifference < 0
        || (monthDifference === 0 && today.getDate() < birthDate.getDate())
      ) {
        age--
      }
      return age
    },
    pendingInvoicesTotalAmount() {
      return this.pendingInvoices.reduce((total, invoice) => {
        return total + (parseFloat(invoice.amount) || 0)
      }, 0)
    },
  },

  watch: {
    patientCard() {
      if (!this.patientCard.is_active) {
        console.log('response3')
        return this.getPatientCard()
      }
    },
    'EditForm.type': {
      handler(newType, oldType) {
        // Reload doctors when card type changes
        if (newType !== oldType) {
          this.selectedDoctors = [] // Clear selected doctors
          // Clear treatment type for IN patients
          this.treatment_type = ''
          this.getDoctors()
        }
        // Calculate paid_until when NICU is selected
        if (newType === 'NICU') {
          this.calculatePaidUntil()
        }
      },
    },
    treatment_type(newVal, oldVal) {
      if (newVal !== oldVal) {
        // Clear doctors when treatment type changes
        this.selectedDoctors = []
      }
    },
    customDays: {
      handler() {
        // Auto-calculate paid_until when customDays changes (for NICU custom)
        if (
          this.EditForm.type === 'NICU'
          && this.nicuPresetDuration === 'custom'
        ) {
          this.calculatePaidUntil()
        }
      },
    },
    nicuPresetDuration: {
      handler() {
        // Auto-calculate paid_until when preset changes (for NICU)
        if (this.EditForm.type === 'NICU') {
          this.calculatePaidUntil()
        }
      },
    },
    selectedAvailableSurgeries: {
      handler(val) {
        try {
          if (!val) {
            this.patientSurgeryPrice = 0
            this.patientSurgeryDateTime = ''
            return
          }
          const found = this.availableSurgeries.find(
            s => s.id === val.id || s.id === val,
          )
          this.patientSurgeryPrice = found && found.price ? Number(found.price) : 0
        } catch (e) {
          this.patientSurgeryPrice = 0
          this.patientSurgeryDateTime = ''
        }
      },
    },
  },
  mounted() {
    this.getDoctors()
    this.getAllSurgeries()
  },
  methods: {
    getNestedProperty(obj, path, defaultValue = '') {
      const result = path
        .split('.')
        .reduce(
          (acc, part) => (acc && acc[part] ? acc[part] : defaultValue),
          obj,
        )

      // console.log("result", result);
      return result
    },
    async showConfirmationModal() {
      const patientId = this.$route.params.id || this.EditForm.patient_id
      if (!patientId) {
        // Fallback to original behavior if no patient ID
        this.$bvModal.show('modal-confirmation-stop-card')
        return
      }

      this.loadingPendingInvoices = true
      this.pendingInvoices = []
      this.pendingDoctors = []
      this.pendingPatientInfo = null
      this.selectedPendingDoctor = null
      this.pendingInvoicesError = null

      try {
        const response = await this.$http.get(
          `/patient/${patientId}/pending-invoices-with-doctors`,
        )

        this.pendingPatientInfo = response.data.patient || null
        this.pendingInvoices = response.data.pending_invoices || []
        this.pendingDoctors = response.data.doctors || []

        // Auto-select first doctor if available
        if (this.pendingDoctors.length > 0) {
          this.selectedPendingDoctor = this.pendingDoctors[0]
        }

        // Branch based on invoice and doctor availability
        if (this.pendingInvoices.length === 0) {
          // No pending invoices - proceed directly to stop card confirmation
          this.$bvModal.show('modal-confirmation-stop-card')
        } else if (this.pendingInvoices.length > 0 && this.pendingDoctors.length > 0) {
          // Has invoices and doctors - show modal with doctor selection
          this.$bvModal.show('modal-pending-invoices-warning')
        } else if (this.pendingInvoices.length > 0 && this.pendingDoctors.length === 0) {
          // Has invoices but no doctors - show informational modal
          this.$bvModal.show('modal-pending-invoices-warning')
        }
      } catch (error) {
        console.error('Error fetching pending invoices:', error)
        this.pendingInvoicesError = error.response?.data?.message || this.$t('common.error-occurred')
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            variant: 'danger',
            text: this.pendingInvoicesError,
          },
        })
        // Fallback to original behavior on error
        this.$bvModal.show('modal-confirmation-stop-card')
      } finally {
        this.loadingPendingInvoices = false
      }
    },
    show(item, mode) {
      if (mode === 'create') {
        this.createMode = true
        this.EditForm = {
          full_companion_name: '',
          companion_phone: '',
          patient_case_details: '',
          type: '',
          patient_id: this.$route.params.id,
          child_name: '',
          _method: 'POST',
        }
      } else {
        this.createMode = false

        this.EditForm.id = item.id
        this.EditForm.full_companion_name = item.full_companion_name
        this.EditForm.companion_phone = item.companion_phone
        this.EditForm.patient_case_details = item.patient_case_details
        this.EditForm.patient_id = item.patient_id
        this.EditForm.type = this.$route.params.id
        this.EditForm._method = 'PUT'
      }
      this.$bvModal.show('modal-patient-card-edit')
    },
    showVistNumberModel() {
      this.$bvModal.show('modal-patient-visit-hospital')
    },
    getDoctors() {
      // Fetch doctors based on patient type - matching RegisterPatient.vue
      const endpoint = this.EditForm.type === 'NICU'
        ? 'doctor/nicu?take=10'
        : 'doctor?take=100'

      this.$http
        .get(endpoint)
        .then(response => {
          this.doctors = response.data.data.map(doctor => ({
            id: doctor.id,
            name: doctor.name,
            type: doctor.type || null,
          }))
        })
        .catch(error => {
          console.error('Error fetching doctors:', error)
        })
    },
    calculatePaidUntil() {
      // Calculate paid_until based on current time and duration (days)
      // Set start_time to current time
      const now = new Date()
      const year = now.getFullYear()
      const month = String(now.getMonth() + 1).padStart(2, '0')
      const day = String(now.getDate()).padStart(2, '0')
      const hours = String(now.getHours()).padStart(2, '0')
      const minutes = String(now.getMinutes()).padStart(2, '0')
      const seconds = String(now.getSeconds()).padStart(2, '0')

      // Set start_time to current datetime (format: YYYY-MM-DD HH:mm:ss)
      this.EditForm.start_time = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`

      let daysToAdd = 1 // Default to 1 day

      if (this.nicuPresetDuration === 'custom') {
        daysToAdd = this.customDays || 1
      }

      // Convert days to hours and add to current time
      const hoursToAdd = daysToAdd * 24
      const endDate = new Date(now.getTime() + hoursToAdd * 60 * 60 * 1000)

      // Format as datetime string (YYYY-MM-DD HH:mm:ss) - matching backend format
      const endYear = endDate.getFullYear()
      const endMonth = String(endDate.getMonth() + 1).padStart(2, '0')
      const endDay = String(endDate.getDate()).padStart(2, '0')
      const endHours = String(endDate.getHours()).padStart(2, '0')
      const endMinutes = String(endDate.getMinutes()).padStart(2, '0')
      const endSeconds = String(endDate.getSeconds()).padStart(2, '0')

      // Format as "YYYY-MM-DD HH:mm:ss" for backend (space instead of T)
      this.EditForm.paid_until = `${endYear}-${endMonth}-${endDay} ${endHours}:${endMinutes}:${endSeconds}`
    },
    async getAllSurgeries() {
      await this.getList('surgery?sort=-id&take=1000')
      this.availableSurgeries = this.all_data.data
      this.availableSurgeriesOptions = []
      this.availableSurgeries.forEach(item => {
        this.availableSurgeriesOptions.push({
          id: item.id,
          title: item.title,
        })
      })
    },
    showCreateSurgeryModal() {
      this.$bvModal.show('modal-create-surgery')
    },
    async createSurgery() {
      this.loadingSurgery = true
      try {
        this.paramsForSurgery.form = this.surgeryForm
        this.paramsForSurgery.path = '/surgery'
        this.$store
          .dispatch('create', this.paramsForSurgery)
          .then(async surgeryResponse => {
            // Link doctors to the created surgery
            const doctorSurgeryPromises = this.selectedDoctors.map(doctor => {
              this.paramsForDoctorSurgery.form = {
                doctor_id: doctor.id,
                surgery_id: surgeryResponse.data.data.id,
                price: 0,
              }
              this.paramsForDoctorSurgery.path = '/doctorSurgery'
              return this.$store.dispatch(
                'create',
                this.paramsForDoctorSurgery,
              )
            })

            await Promise.all(doctorSurgeryPromises)
            this.loadingSurgery = false
            this.$bvModal.hide('modal-create-surgery')
            await this.getAllSurgeries()
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: 'Success',
                variant: 'success',
                text: this.$t(
                  'reception.surgery-created-and-linked-to-doctors-successfully',
                ),
              },
            })
          })
          .catch(error => {
            this.loadingSurgery = false
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: 'Error',
                variant: 'danger',
                text:
                  error.response?.data?.message
                  || this.$t(
                    'reception.surgery-created-and-linked-to-doctors-error',
                  ),
              },
            })
          })
      } catch (error) {
        this.loadingSurgery = false
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: 'Error',
            variant: 'danger',
            text:
              error.response?.data?.message
              || this.$t('reception.surgery-created-and-linked-to-doctors-error'),
          },
        })
      }
    },
    async createPatientSurgery(response) {
      try {
        // Use the user-provided surgery date/time if available, otherwise use current time
        let surgeryDateTime
        if (this.patientSurgeryDateTime) {
          // Convert from datetime-local format (YYYY-MM-DDTHH:mm) to backend format (YYYY-MM-DD HH:mm)
          const dateObj = new Date(this.patientSurgeryDateTime)
          surgeryDateTime = `${dateObj.getFullYear()}-${String(
            dateObj.getMonth() + 1,
          ).padStart(2, '0')}-${String(dateObj.getDate()).padStart(
            2,
            '0',
          )} ${String(dateObj.getHours()).padStart(2, '0')}:${String(
            dateObj.getMinutes(),
          ).padStart(2, '0')}`
        } else {
          // Fallback to current time if no date is provided
          const now = new Date()
          surgeryDateTime = `${now.getFullYear()}-${String(
            now.getMonth() + 1,
          ).padStart(2, '0')}-${String(now.getDate()).padStart(
            2,
            '0',
          )} ${String(now.getHours()).padStart(2, '0')}:${String(
            now.getMinutes(),
          ).padStart(2, '0')}`
        }

        // Get the patient card ID from response
        // In AddNewPatientCard, the response structure is response.data.data.id
        const patientCardId = response.data.data.id

        // Get surgery ID - handle both object and ID cases
        const surgeryId = this.selectedAvailableSurgeries.id || this.selectedAvailableSurgeries

        const surgeryPayload = {
          patient_card_id: patientCardId,
          surgery_id: surgeryId,
          surgery_date_time: surgeryDateTime,
          doctors: this.selectedDoctors.map(doctor => ({
            id: doctor.id,
            type: doctor.type,
          })),
          // include the price paid/charged for this surgery
          price: Number(this.patientSurgeryPrice) || 0,
        }

        await this.$store.dispatch('create', {
          form: surgeryPayload,
          path: 'patient-surgery',
        })

        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: 'Surgery Scheduled',
            variant: 'success',
          },
        })
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: 'Surgery Creation Error',
            variant: 'danger',
            text:
              error.response?.data?.message
              || this.$t('reception.surgery-creation-error'),
          },
        })
        throw error // Re-throw error if you need to handle it in the caller
      }
    },
    showMoveInvoicesConfirmation() {
      if (!this.selectedPendingDoctor || this.pendingInvoices.length === 0) {
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            variant: 'warning',
            text: this.$t('reception.please-select-doctor') || 'يرجى اختيار طبيب',
          },
        })
        return
      }

      // Show confirmation modal
      this.$bvModal.show('modal-confirm-move-invoices')
    },
    async confirmMoveInvoicesAndStopCard() {
      if (!this.selectedPendingDoctor || this.pendingInvoices.length === 0) {
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            variant: 'warning',
            text: this.$t('reception.please-select-doctor') || 'يرجى اختيار طبيب',
          },
        })
        return
      }

      try {
        // Move all pending invoices to the selected doctor in one bulk request
        const payload = {
          invoices: this.pendingInvoices.map(invoice => ({
            id: invoice.id,
            doctor_dept_id: this.selectedPendingDoctor.id,
          })),
        }

        await this.$http.post('/invoices/bulkUpdate', payload)

        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.success'),
            variant: 'success',
            text: this.$t('reception.invoices-moved-successfully') || 'تم نقل الفواتير بنجاح',
          },
        })

        // Hide both modals and proceed to stop card
        this.$bvModal.hide('modal-confirm-move-invoices')
        this.$bvModal.hide('modal-pending-invoices-warning')
        this.$bvModal.show('modal-confirmation-stop-card')
      } catch (error) {
        console.error('Error moving invoices:', error)
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            variant: 'danger',
            text: error.response?.data?.message || this.$t('reception.error-moving-invoices') || 'حدث خطأ أثناء نقل الفواتير',
          },
        })
      }
    },

    confirmStopCardWithoutMoving() {
      // Hide pending invoices modal and show stop card confirmation
      this.$bvModal.hide('modal-pending-invoices-warning')
      this.$bvModal.show('modal-confirmation-stop-card')
    },

    resetPendingInvoicesState() {
      // Reset state when modal is closed
      this.pendingInvoices = []
      this.pendingDoctors = []
      this.pendingPatientInfo = null
      this.selectedPendingDoctor = null
      this.pendingInvoicesError = null
    },

    getInvoiceTypeLabel(type) {
      const typeMap = {
        invoice_out_clinic: this.$t('reception.out-clinic') || 'عيادة خارجية',
        invoice_nicu: this.$t('incubator.nicu') || 'حاضنة',
        invoice_test: this.$t('lab.test') || 'فحص',
        invoice_surgery: this.$t('surgery.surgery') || 'جراحة',
        invoice_emergency: this.$t('reception.emergency') || 'طوارئ',
        invoice_insurance_surgery: this.$t('surgery.surgery-insurance') || 'عملية جراحية (تأمين)',
        invoice_patient_card_test: this.$t('lab.test-invoices') || 'فاتورة   تحليل',
      }
      return typeMap[type] || type || 'N/A'
    },

    formatPrice(price) {
      return price ? Number(price).toLocaleString() : '0'
    },

    stopCard() {
      this.params.path = `/stop/patientCard/${this.patientCard.id}`
      this.params.form = this.EditForm
      this.$store
        .dispatch('put', this.params)
        .then(response => {
          this.showPrivewInvoice = false
          if (this.all_response.data.status) {
            window.location.reload()
            console.log('response1')
            this.getPatientCard()
            this.$bvModal.hide('modal-confirmation-stop-card')
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: 'Card Stopped',
                variant: 'success',
              },
            })
          } else {
            this.showPrivewInvoice = true
            // if (
            //   this.all_response.data.message ===
            //   "There are still pending invoices"
            // ) {
            //   this.$bvModal.hide("modal-confirmation-stop-card");
            // }
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: `${this.all_response.data.message}`,
                variant: 'danger',
              },
            })
          }
        })
        .catch(error => {
          if (error.response.status === 400) {
            this.showPrivewInvoice = true
            for (const item in error.response.data) {
              this.$toast({
                component: ToastificationContent,
                position: 'top-right',
                props: {
                  title: 'validation error',
                  variant: 'danger',
                  text: error.response.data[item][0],
                },
              })
            }
          }
        })
    },

    showPhoneObj(obj, countryCallingCodeCheck) {
      this.isNotValid = !obj.isValid
      countryCallingCodeCheck
        ? (this.countryCallingCodeFirst = obj.countryCallingCode)
        : (this.countryCallingCodeSecond = obj.countryCallingCode)
    },

    submitItem() {
      for (const item in this.errorForm) {
        this.errorForm[item] = ''
        if (this.EditForm[item] == '' || this.EditForm[item] == null) delete this.EditForm[item]
      }

      // Handle doctors based on type - matching RegisterPatient.vue logic
      if (this.EditForm.type === 'NICU') {
        // For NICU, send both doctors array and doctor_incubator_id
        this.EditForm.doctors = [
          ...this.selectedDoctors.map(doctor => doctor.id),
        ]
        // Set doctor_incubator_id from the first selected doctor
        if (this.selectedDoctors.length > 0) {
          this.EditForm.doctor_incubator_id = this.selectedDoctors[0].id
        }
      } else {
        // For other types, use doctors array only
        this.EditForm.doctors = [
          ...this.selectedDoctors.map(doctor => doctor.id),
        ]
        // Ensure doctor_incubator_id is not sent for non-NICU types
        delete this.EditForm.doctor_incubator_id
      }

      // Delete room_id if IN type but treatment_type is None (before ID conversion)
      if (this.EditForm.type === 'IN' && this.treatment_type === 'None') {
        delete this.EditForm.room_id
      }

      // Ensure room_id is just the ID string, not the full object
      if (this.EditForm.room_id && typeof this.EditForm.room_id === 'object') {
        this.EditForm.room_id = this.EditForm.room_id.id
      }

      // Ensure incubator_id is just the ID string, not the full object
      if (
        this.EditForm.incubator_id
        && typeof this.EditForm.incubator_id === 'object'
      ) {
        this.EditForm.incubator_id = this.EditForm.incubator_id.id
      }

      // Delete fields for non-IN and non-NICU types
      if (this.EditForm.type !== 'IN' && this.EditForm.type !== 'NICU') {
        delete this.EditForm.doctor_id
        delete this.EditForm.room_id
        delete this.EditForm.incubator_id
        delete this.EditForm.start_time
        delete this.EditForm.paid_until
        delete this.EditForm.end_time
      }

      // Delete room_id for NICU since we use incubator_id instead
      if (this.EditForm.type === 'NICU') {
        delete this.EditForm.room_id
      }

      // Delete incubator_id and start_time for non-NICU types
      if (this.EditForm.type !== 'NICU') {
        delete this.EditForm.incubator_id
        delete this.EditForm.start_time
        delete this.EditForm.paid_until
        delete this.EditForm.end_time
      } else {
        // Calculate paid_until for NICU based on start_time and duration
        this.calculatePaidUntil()
        // Delete end_time if it exists (we use paid_until instead)
        delete this.EditForm.end_time
      }

      console.log('Payload being sent:', this.EditForm)
      if (this.createMode) {
        (this.params.form = this.EditForm),
        (this.params.path = '/patientCard'),
        this.$store
          .dispatch('create', this.params)
          .then(async response => {
            // If surgery is selected, create patient surgery
            if (
              this.EditForm.type === 'IN'
                && this.treatment_type === 'Surgery'
                && this.selectedAvailableSurgeries
            ) {
              await this.createPatientSurgery(response)
            }
            window.location.reload()
          })
          .catch(error => {
            if (error.response.status === 400) {
              for (const item in error.response.data) {
                this.$toast({
                  component: ToastificationContent,
                  position: 'top-right',
                  props: {
                    title: 'validation error',
                    variant: 'danger',
                    text: error.response.data[item][0],
                  },
                })
              }
            }
          })
      } else {
        (this.params.form = this.EditForm),
        (this.params.path = `/patientCard/${this.EditForm.id}`),
        this.$store
          .dispatch('update', this.params)
          .then(response => {
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: 'Patient Card Added',
                variant: 'success',
              },
            })
          })
          .catch(error => {
            if (error.response.status === 400) {
              for (const item in error.response.data) {
                this.$toast({
                  component: ToastificationContent,
                  position: 'top-right',
                  props: {
                    title: 'validation error',
                    variant: 'danger',
                    text: error.response.data[item][0],
                  },
                })
              }
            }
          })
      }
    },
    submitVistNumber() {
      const data = {
        patient_id: this.$route.params.id,
        number_of_stays_before_activating_the_system:
          this.number_of_stays_before_activating_the_system,
      };
      (this.params.form = data),
      (this.params.path = `/patient/${this.$route.params.id}`),
      this.$store
        .dispatch('put', this.params)
        .then(response => {
          console.log('response2')
          this.getPatientCard()
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: 'Patient Visit Added',
              variant: 'success',
            },
          })
        })
        .catch(error => {
          if (error.response.status === 400) {
            for (const item in error.response.data) {
              this.$toast({
                component: ToastificationContent,
                position: 'top-right',
                props: {
                  title: 'validation error',
                  variant: 'danger',
                  text: error.response.data[item][0],
                },
              })
            }
          }
        })
    },
    printInpatientForm() {
      this.$refs.currentPatientPdf.generatePdf()
    },
    formatDate(date) {
      const d = new Date(date)
      let month = `${d.getMonth() + 1}`
      let day = `${d.getDate()}`
      const year = d.getFullYear()

      if (month.length < 2) month = `0${month}`
      if (day.length < 2) day = `0${day}`
      return [year, month, day].join('-')
    },
    async getPatientCard() {
      this.loading = true
      await this.getPatient(
        `patient/${this.$route.params.id}/checkout-printable-card?include=patientCards`,
      )
      // api/patient/:patient_id/checkout-printable-card
      this.loading = false
      for (const item in this.all_data) {
        if (this.all_data[item] == null) this.all_data[item] = ''
      }
      this.patientCardData = this.all_data.patient_cards.find(
        x => x.type !== 'out',
      )
      console.log('this.patientCardData', this.patientCardData)
      this.number_of_total_stays_in_hospital = this.all_data.number_of_total_stays_in_hospital
      this.number_of_stays_before_activating_the_system = this.all_data.number_of_stays_before_activating_the_system
      this.latest_unactive_patient_card = this.all_data.latest_unactive_patient_card
      this.getGovernorateName(this.all_data.governorate_id)
    },
    async getGovernorateName(governorateId) {
      try {
        const governorate = await this.getList(`governorate/${governorateId}`)
        this.governorateName = governorate.data.name
        console.log('governorateName', this.governorateName)
      } catch (error) {
        console.error('Error fetching governorate name:', error)
        // Handle the error appropriately
      }
    },
    async changeRoom() {
      this.changeRoomForm = {
        room_id: null,
        days: 0,
      }
      console.log(
        'patientCard',
        this.patientCard.patient_room[this.patientCard.patient_room.length - 1]
          .id,
      )
      try {
        // Fetch available rooms
        const response = await this.getList(
          'available-room?include=floor&take=2000',
        )
        this.availableRooms = response.data.data
        this.$bvModal.show('modal-change-room')
      } catch (error) {
        console.error('Error fetching rooms:', error)
      }
    },
    async submitChangeRoom() {
      this.loading = true
      try {
        const urlEncodedData = new URLSearchParams()
        urlEncodedData.append('room_id', this.changeRoomForm.room_id)
        urlEncodedData.append('days', this.changeRoomForm.days)

        await this.$store.dispatch('put', {
          form: urlEncodedData.toString(),
          path: `change-patient-room/${
            this.patientCard.patient_room[
              this.patientCard.patient_room.length - 1
            ].id
          }`,
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        })

        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: 'Room Changed',
            variant: 'success',
            text: 'Patient room changed successfully',
          },
        })

        this.$bvModal.hide('modal-change-room')
      } catch (error) {
        if (error.response?.status === 400) {
          for (const item in error.response.data) {
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: 'Validation Error',
                variant: 'danger',
                text: error.response.data[item][0],
              },
            })
          }
        }
      } finally {
        this.loading = false
      }
    },
    async changeSurgery() {
      this.loadingChangeSurgery = true
      try {
        await this.getList('surgery?take=100')
        this.availableSurgeriesOptions = this.all_data.data.map(surgery => ({
          id: surgery.id,
          title: surgery.title,
        }))
        this.$bvModal.show('modal-change-surgery')
      } catch (error) {
        console.error('Error fetching surgeries:', error)
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: 'Error',
            variant: 'danger',
            text: 'Failed to fetch surgeries',
          },
        })
      } finally {
        this.loadingChangeSurgery = false
      }
    },
    async submitChangeSurgery() {
      if (!this.selectedSurgery) {
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: 'Validation Error',
            variant: 'danger',
            text: 'Please select a surgery',
          },
        })
        return
      }

      this.loadingChangeSurgery = true
      try {
        this.params.form = {
          surgery_id: this.selectedSurgery,
          _method: 'PUT',
        }
        this.params.path = `/patient-surgery/${
          this.surgeries[this.surgeries.length - 1].id
        }`

        await this.$store.dispatch('put', this.params)

        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: 'Success',
            variant: 'success',
            text: 'Surgery changed successfully',
          },
        })

        this.$emit('refreshPatientCard')
        this.$bvModal.hide('modal-change-surgery')
      } catch (error) {
        if (error.response?.status === 400) {
          Object.keys(error.response.data).forEach(item => {
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: 'Validation Error',
                variant: 'danger',
                text: error.response.data[item][0],
              },
            })
          })
        }
      } finally {
        this.loadingChangeSurgery = false
      }
    },
  },
}
</script>

<style scoped>
.mb-md-0 {
  margin-top: 8px;
}

.start-box {
  display: flex;
  flex-direction: column;
  align-items: start;
  margin: 0 1rem;
  position: absolute;
  right: 1%;
  top: -13.5%;
}

table {
  width: 94%;
  border: 1px solid;
  font-size: 11px;
}

.no-border {
  border: 0;
}

.square-dark {
  background-color: rgba(0, 0, 0, 0.2);
}

tr,
th,
td {
  border: 1px solid;
  width: 10px;
  height: 10px;
  text-align: center;
}

.font-modify {
  letter-spacing: normal !important;
}

.pdf-table {
  page-break-inside: avoid;
}

.square {
  width: 30px;
  /* Smaller width for squares */
  height: 30px;
  /* Smaller height for squares */
  background-color: #000;
  /* Color of the square */
  display: inline-block;
  /* Allows us to set width and height */
}

.centered-text {
  text-align: center;
  border: 2px solid #000;
  /* Border for the cell */
}

#table-above {
  border-collapse: collapse;
  /* Removes space between borders */
  font-size: 12px;
  /* Smaller font size for the entire table */
}

@media print {
  @page {
  }

  .pdf-table {
    page-break-before: avoid;
    page-break-inside: avoid;
  }
}

.pdf-table:first-child {
  margin-top: 1em;
  /* Adjust the value as needed */
  page-break-inside: avoid;
}
.font-modify {
  letter-spacing: normal !important;
}
.rotated-text {
  transform: rotate(90deg);
  white-space: nowrap;
  text-align: center;
  color: #000;
  font-size: 12px;
}

/* Treatment Type Cards */
.treatment-type-cards {
  margin-top: 0.5rem;
}

.treatment-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1.5rem 1rem;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  background-color: #ffffff;
  cursor: pointer;
  transition: all 0.3s ease;
  min-height: 120px;
  text-align: center;
}

.treatment-card:hover {
  border-color: #667eea;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.15);
  transform: translateY(-2px);
}

.treatment-card.active {
  border-color: #2ecc71;
  background-color: #f0fdf4;
  box-shadow: 0 4px 12px rgba(46, 204, 113, 0.2);
}

.treatment-card.active .treatment-card-title {
  color: #2ecc71;
  font-weight: 600;
}

.treatment-card-title {
  font-size: 1rem;
  font-weight: 500;
  color: #34495e;
  margin-top: 0.5rem;
}

.treatment-card svg {
  color: #667eea;
  transition: color 0.3s ease;
}

.treatment-card.active svg {
  color: #2ecc71;
}
</style>
